import { Steps } from '@octano/global-ui';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { StepProvider } from '../../../../components/step/StepProvider';
import { useStepState } from '../../../../components/step/useStepState';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import withProvider from '../../../../providers/withProvider';

import {
  AcademicOfferWizardProvider,
  useAcademicOfferWizard,
} from './AcademicOfferWizardContext';
import { getAcademicOfferCurrentStep } from '../api';
import { CeAcademicOfferStepEnum } from '../types';
import { useAcademicOfferCreateSteps } from '../hooks/useAcademicOfferCreateSteps';
import { AcademicOfferEditHeader } from '../parts/AcademicOfferEditHeader';

const STEP_ID_MAP: Record<CeAcademicOfferStepEnum, string> = {
  [CeAcademicOfferStepEnum.Offer]: 'offer',
  [CeAcademicOfferStepEnum.Sections]: 'sections',
  [CeAcademicOfferStepEnum.Schedule]: 'schedule',
  [CeAcademicOfferStepEnum.Summary]: 'summary',
};

function AcademicOfferWizardInner() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { setCeAcademicOfferId } = useAcademicOfferWizard();
  const { currentStep, setCurrentStep } = useStepState();
  const {
    namesSteps,
    CurrentStepComponent,
    getIndexStep,
  } = useAcademicOfferCreateSteps();

  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState(!isNew);

  const fetchCurrentStep = useCallback(async () => {
    if (isNew) {
      return;
    }

    const offerId = parseInt(id, 10);
    setCeAcademicOfferId(offerId);

    const { data, error } = await getAcademicOfferCurrentStep(offerId);
    if (data) {
      const stepId = STEP_ID_MAP[data.currentStep];
      const index = getIndexStep(stepId);
      setCurrentStep(index >= 0 ? index : 0);
      setErrorLoading(undefined);
    } else if (error) {
      setErrorLoading(error.code);
    }
    setLoading(false);
  }, [
    id,
    isNew,
    setCeAcademicOfferId,
    getIndexStep,
    setCurrentStep,
    setErrorLoading,
    setLoading,
  ]);

  useEffect(() => {
    if (loading) {
      fetchCurrentStep();
    }
  }, [loading, fetchCurrentStep]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => setLoading(true)}
        loadingAction={loading}
      />
    );
  }

  if (loading) {
    return <Loading insideCard />;
  }

  return (
    <>
      <Row>
        <Col xs={12} className="pb-4">
          <Steps steps={namesSteps} currentStep={currentStep} />
        </Col>
        <Col xs={12} className="pt-2">
          <Card className="p-4 mt-2 mx-3">
            <AcademicOfferEditHeader />
            {CurrentStepComponent && <CurrentStepComponent />}
          </Card>
        </Col>
      </Row>
    </>
  );
}

const AcademicOfferWizardWithStep = withProvider(
  AcademicOfferWizardInner,
  StepProvider,
);

export function CeAcademicOfferWizard() {
  const { id } = useParams<{ id: string }>();

  return (
    <AcademicOfferWizardProvider>
      <AcademicOfferWizardWithStep key={id} />
    </AcademicOfferWizardProvider>
  );
}
